// To'lov provayderi sozlamalari — `psp.read` / `psp.configure`.
//
// BACKEND KONTRAKTI (xsib-payments, `admin` paketi):
//   GET /api/payment-providers                       -> PspProviderView[]
//   PUT /api/payment-providers/{id}                  -> PspProviderView
//   PUT /api/payment-providers/{id}/api-key-fingerprint { secret } -> OperationResult
//
// ⚠️ YO'L `/api/payment-providers`, `/api/psp` EMAS: `/api/psp/**` gateway'da
// ANONIM zona (PSP callback'lari tokensiz keladi). Sozlamalarni o'sha
// prefiksga qo'yish "faqat SUPERADMIN" talabini (docs/04-ROLES.md §6)
// butunlay bekor qilardi.
//
// ⚠️ SIR HECH QACHON KELMAYDI VA KETMAYDI.
// `api_key_hash` ham qaytmaydi: u "faqat hash" bo'lgani uchun zararsizdek
// ko'rinadi, lekin oflayn taxmin uchun material — panel esa brauzerda
// ishlaydi. O'rniga IKKI XULOSA keladi: `secretConfigured` va
// `secretMatchesFingerprint`. Ular bilan sirni tiklab bo'lmaydi.
//
// NAS siri uchun aynan shu naqsh qo'llangan (`features/nas/nasApi.ts`) —
// bu yerda u bir qadam oldinga surildi: NAS'da sir O'RNATILADI, bu yerda
// esa u umuman bazada emas (konfiguratsiyada), shuning uchun panel faqat
// BARMOQ IZINI moslashtira oladi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { moneySchema, toMoney, uuidSchema } from '@/shared/api/schemas'
import type { Money } from '@/shared/lib/money'

const providerSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  state: z.string(),
  sandbox: z.boolean(),
  minAmount: moneySchema,
  maxAmount: moneySchema,
  ipAllowlist: z.array(z.string()),
  rateLimitPerMinute: z.number(),
  secretConfigured: z.boolean(),
  secretMatchesFingerprint: z.boolean(),
  createdAt: z.string().nullish(),
})

export type PspSecretHealth = 'OK' | 'MISMATCH' | 'MISSING'

export interface PspProvider {
  id: string
  code: string
  name: string
  state: 'ACTIVE' | 'SUSPENDED'
  sandbox: boolean
  minAmount: Money
  maxAmount: Money
  ipAllowlist: string[]
  rateLimitPerMinute: number
  secretHealth: PspSecretHealth
  createdAt: string | null
}

export interface PspProviderUpdate {
  name: string
  state: 'ACTIVE' | 'SUSPENDED'
  sandbox: boolean
  /** MINOR birlikda — forma MAJOR dan `majorToMinor` bilan o'giradi */
  minAmount: number
  maxAmount: number
  ipAllowlist: string[]
  rateLimitPerMinute: number
}

/**
 * Javobda sir yoki uning hash'i bormi — bo'lsa BU NOSOZLIK.
 *
 * `nasApi.assertNoSecret` bilan bir xil qorovul: panel uni ko'rsatmaydi,
 * lekin backend uni umuman qaytarmasligi kerak va bu jimgina o'tib
 * ketmasligi kerak.
 */
function assertNoSecret(body: unknown): void {
  const rows: unknown[] = Array.isArray(body) ? body : [body]
  for (const row of rows) {
    if (typeof row === 'object' && row !== null) {
      for (const forbidden of ['secret', 'apiKey', 'apiKeyHash']) {
        if (forbidden in row) {
          console.error(
            `[xsib] XAVFSIZLIK: /api/payment-providers javobida \`${forbidden}\` maydoni bor. ` +
              'Panel uni ko\'rsatmaydi, lekin backend uni umuman qaytarmasligi kerak.',
          )
          return
        }
      }
    }
  }
}

function toProvider(raw: z.infer<typeof providerSchema>): PspProvider {
  const health: PspSecretHealth = !raw.secretConfigured
    ? 'MISSING'
    : raw.secretMatchesFingerprint
      ? 'OK'
      : 'MISMATCH'

  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    state: raw.state === 'SUSPENDED' ? 'SUSPENDED' : 'ACTIVE',
    sandbox: raw.sandbox,
    minAmount: toMoney(raw.minAmount),
    maxAmount: toMoney(raw.maxAmount),
    ipAllowlist: raw.ipAllowlist,
    rateLimitPerMinute: raw.rateLimitPerMinute,
    secretHealth: health,
    createdAt: raw.createdAt ?? null,
  }
}

export async function fetchPspProviders(signal?: AbortSignal): Promise<PspProvider[]> {
  const response = await api.get<unknown>('/api/payment-providers', { signal })
  assertNoSecret(response.data)
  return z.array(providerSchema).parse(response.data).map(toProvider)
}

export async function updatePspProvider(
  id: string,
  request: PspProviderUpdate,
): Promise<PspProvider> {
  const response = await api.put<unknown>(`/api/payment-providers/${id}`, request)
  assertNoSecret(response.data)
  return toProvider(providerSchema.parse(response.data))
}

/**
 * Barmoq izini konfiguratsiyadagi kalit bilan moslashtiradi.
 *
 * ⚠️ Bu "kalitni almashtirish" EMAS. Kalitning o'zi servis
 * konfiguratsiyasida (`.env` / Swarm secret) va uni paneldan
 * o'zgartirib bo'lmaydi — imzo HMAC bilan tekshiriladi, ya'ni server
 * kalitning O'ZIGA muhtoj.
 *
 * Tartib: PSP bilan yangi kalit kelishiladi → konfiguratsiya yangilanadi
 * va deploy qilinadi → shundan KEYIN bu chaqiruv bazadagi barmoq izini
 * moslashtiradi. Kalit mos kelmasa server rad etadi va bazani
 * TEGMAYDI — noto'g'ri barmoq izi to'lovlarni to'xtatib qo'yardi.
 */
export async function resyncPspFingerprint(
  id: string,
  secret: string,
): Promise<OperationResult> {
  const response = await api.put<unknown>(`/api/payment-providers/${id}/api-key-fingerprint`, {
    secret,
  })
  return operationResultSchema.parse(response.data)
}
